import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { api, type MailConversation } from '../../lib/api'
import {
  DeferredMailAction,
  type MailDeferredAction,
  mailActionErrorMessage,
  performMailAction,
} from '../../lib/mail-swipe'
import { notify } from '../../lib/notify'

export function useMailUndo() {
  const qc = useQueryClient()
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const toastIds = useRef(new Map<string, string>())
  const sequence = useRef(0)
  const pending = useRef(new Map<string, DeferredMailAction>())
  useEffect(
    () => () => {
      for (const [key, action] of pending.current) {
        action.undo()
        const toastId = toastIds.current.get(key)
        if (toastId) toast.dismiss(toastId)
      }
      pending.current.clear()
    },
    [],
  )
  function schedule(thread: MailConversation, action: MailDeferredAction) {
    const accountId = thread.latest.accountId
    const key = `${accountId}:${thread.latest.folderPath}:${thread.threadRoot}`
    if (pending.current.has(key)) return
    const toastId = `${key}:${++sequence.current}`
    toastIds.current.set(key, toastId)
    const restore = () =>
      setHidden((old) => {
        const next = new Set(old)
        next.delete(key)
        return next
      })
    setHidden((old) => new Set(old).add(key))
    pending.current.set(
      key,
      new DeferredMailAction(() => {
        toast.dismiss(toastId)
        void (async () => {
          try {
            await performMailAction(
              api,
              accountId,
              thread.messages.map((m) => m.id),
              action,
            )
          } catch (error) {
            notify.error(mailActionErrorMessage(action, error))
          } finally {
            // Refetch even after a partial move; only the remaining messages reappear.
            await qc.invalidateQueries({ queryKey: ['mail-messages', accountId] })
            // The card may come from the unified inbox (#535); its account is the thread's own.
            await qc.invalidateQueries({ queryKey: ['mail-unified'] })
            await qc.invalidateQueries({ queryKey: ['mail-unread-count'] })
            await qc.invalidateQueries({ queryKey: ['mail-folders', accountId] })
            pending.current.delete(key)
            toastIds.current.delete(key)
            toast.dismiss(toastId)
            restore()
          }
        })()
      }),
    )
    toast(
      typeof action === 'object'
        ? `Konversation kommt ${action.label} zurück`
        : action === 'archive'
          ? 'Konversation wird archiviert'
          : 'Konversation wird in den Papierkorb verschoben',
      {
        id: toastId,
        duration: 5000,
        action: {
          label: 'Rückgängig',
          onClick: () => {
            if (!pending.current.get(key)?.undo()) return
            pending.current.delete(key)
            toastIds.current.delete(key)
            toast.dismiss(toastId)
            restore()
          },
        },
      },
    )
  }
  return {
    schedule,
    isHidden: (thread: MailConversation) =>
      hidden.has(`${thread.latest.accountId}:${thread.latest.folderPath}:${thread.threadRoot}`),
  }
}
